import { OrderPaymentType } from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { paymentDetailAtom } from "Order/Atom/paymentAtom";
import { useCreateOrderPaymentMutation } from "Order/Modules/Payment/Api/Mutation/useCreatePaymentMutation";
import { ActionButton } from "Order/Modules/Payment/Components/ActionButton";
import {
	type AddPaymentFormModel,
	getAddPaymentFormModelSchema,
} from "Order/Modules/Payment/Components/Forms/Schema/addPaymentFormSchema";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "@mui/icons-material";
import { Grid2, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const defaultFormValues = {
	dateCreated: dayjs().toISOString(),
	amount: 0,
	type: OrderPaymentType.orderPayment,
};

const paymentResource = Resources.base.payment.form;
const selectOptionsResource = Resources.base.payment.selectOptions;

type AddPaymentFormProps = {
	orderId: number;
};

export function AddPaymentForm({ orderId }: AddPaymentFormProps) {
	const { t } = useResource();
	const [paymentDetail] = useAtom(paymentDetailAtom);

	const form = useForm<AddPaymentFormModel>({
		resolver: zodResolver(getAddPaymentFormModelSchema(t)),
		defaultValues: defaultFormValues,
	});

	const {
		control,
		formState: { errors, isDirty },
	} = form;

	const { mutate: createPayment, isPending } =
		useCreateOrderPaymentMutation(orderId);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitCreatePayment = () => {
		handleServerError(createPayment, {
			onSuccess: () => {
				form.reset(defaultFormValues);
			},
		});
	};
	const hasPaymentCreatePermission = hasPermission(
		UserPermissions.OrderPaymentModule.create,
	);

	useEffect(() => {
		if (paymentDetail?.customerDebt !== undefined) {
			form.setValue("amount", Number(paymentDetail.customerDebt));
		}
	}, [paymentDetail?.customerDebt, form]);

	return (
		<Stack width="100%" gap={1}>
			<Grid2 container mb={1} spacing={{ xs: 0, sm: 2 }}>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<DatePicker
						name="dateCreated"
						label={t(paymentResource.dateCreated)}
						control={control}
						errors={errors}
						disabled={!hasPaymentCreatePermission}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<Select
						id="type"
						name="type"
						label={t(paymentResource.type)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasPaymentCreatePermission}
						fullWidth
						options={
							// 480 - Remove "Order deposit" payment type

							createSelectOptions(
								OrderPaymentType,
								t,
								selectOptionsResource.paymentType,
							).filter((i) => i.value !== OrderPaymentType.orderDepositPayment)
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 3 }}>
					<Input
						id="amount"
						name="amount"
						label={t(paymentResource.amount)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasPaymentCreatePermission}
						isFloat
						isNumber
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 1 }}>
					<ActionButton
						onClick={handleSubmitCreatePayment}
						variant="contained"
						loading={isPending}
						disabled={!hasPaymentCreatePermission || !isDirty}
					>
						<Check />
					</ActionButton>
				</Grid2>
			</Grid2>
		</Stack>
	);
}
